import { useEffect, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { useCompany } from '../context/CompanyContext';
import api from '../api';
import './Dashboard.css';

const EMPTY_FORM = { title: '', note: '', reminder_date: '' };

function NotesReminders() {
  const { selectedCompanyId } = useCompany();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  const refresh = () => {
    if (!selectedCompanyId) return Promise.resolve();
    return api.get('/notes', { params: { company_id: selectedCompanyId } }).then((res) => setRows(res.data));
  };

  useEffect(() => {
    setLoading(true);
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      title: r.title || '',
      note: r.note || '',
      reminder_date: r.reminder_date || '',
    });
    setModalOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedCompanyId) return;
    setSaving(true);
    const payload = { ...form, company_id: selectedCompanyId, reminder_date: form.reminder_date || null };
    const request = editingId ? api.put(`/notes/${editingId}`, payload) : api.post('/notes', payload);
    request
      .then(() => {
        refresh();
        showToast(editingId ? 'Note updated ✓' : 'Note added ✓');
        setModalOpen(false);
      })
      .finally(() => setSaving(false));
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    api.delete(`/notes/${deleteTarget.id}`).then(() => {
      refresh();
      showToast(`${deleteTarget.title} deleted`);
      setDeleteTarget(null);
    });
  };

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">Notes / Reminders</h1>
      </div>

      {toast && <div className="cal-toast">{toast}</div>}

      <div className="dash-card">
        <div className="page-title-row">
          <h2 className="dash-card-title">Your Notes</h2>
          <button className="dash-btn dash-btn-orange" type="button" onClick={openAdd}>
            + Add New Note
          </button>
        </div>

        <table className="dash-table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Note</th>
              <th>Reminder Date</th>
              <th>Created On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td className="dash-empty" colSpan={5}>No notes yet. Add your first one.</td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id}>
                <td>{r.title}</td>
                <td>{r.note || '—'}</td>
                <td>{r.reminder_date || '—'}</td>
                <td>{new Date(r.created_on).toLocaleDateString('en-IN')}</td>
                <td>
                  <div className="table-actions">
                    <button className="dash-btn cal-btn-grey" type="button" onClick={() => openEdit(r)}>
                      Edit
                    </button>
                    <button className="dash-btn-red-outline" type="button" onClick={() => setDeleteTarget(r)}>
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <div className="ncf-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">{editingId ? 'Edit Note' : 'Add New Note'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="field-group">
                <span className="field-label">Title *</span>
                <input
                  className="field-input"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Note</span>
                <textarea
                  className="field-input"
                  rows={4}
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                />
              </div>
              <div className="field-group">
                <span className="field-label">Reminder Date (optional)</span>
                <input
                  type="date"
                  className="field-input"
                  value={form.reminder_date || ''}
                  onChange={(e) => setForm({ ...form, reminder_date: e.target.value })}
                />
              </div>
              <div className="ncf-modal-actions">
                <button type="button" className="dash-btn cal-btn-grey" onClick={() => setModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="dash-btn dash-btn-orange" disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="ncf-modal-overlay" onClick={() => setDeleteTarget(null)}>
          <div className="ncf-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Delete Note</h3>
            <p style={{ fontSize: 14, color: '#374151' }}>
              Are you sure you want to delete <strong>{deleteTarget.title}</strong>? This cannot be undone.
            </p>
            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDeleteTarget(null)}>
                Cancel
              </button>
              <button className="dash-btn dash-btn-orange" style={{ background: '#dc2626' }} type="button" onClick={confirmDelete}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

export default NotesReminders;
